import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import Alert from '../../../DesignSystem/Alert';
import Dialog from '../../../DesignSystem/Dialog';
import TextFieldDefault from '../../Forms/TextFieldDefault';
import ButtonWithLoader from '../../../ButtonWithLoader';
import PostStatusSelect from '../PostStatus/PostStatusSelect';
import { PostCategories, PostStatuses, PostTypes } from '../../../../Utils/Types';
import { replyBtnTextId } from '../../PostSidePrimaryActions/utils';
import AnalyticsService from '../../../../Services/AnalyticsService';
import FormOriginalPost from '../../Forms/Common/FormOriginalPost';
import useCommentAndStatusUpdate from '../../../../Hooks/useCommentAndStatusUpdate';
import withOptional from '../../../../Utils/Posts/Form/withOptional';
import useApiQuery from '../../../../Hooks/useApiQuery';
import queriesConfig from '../../../../Services/queriesConfig';

function CommentAndStatusUpdate({
  open,
  posts,
  preSelectedStatus,
  onClose = () => {},
  onSubmit = () => {},
}: any) {
  const intl = useIntl();

  const [content, setContent] = useState('');
  const [originalPostId, setOriginalPostId] = useState(null);

  const postsCount = posts.length;
  // we take the first post, and suppose they all have the same type
  const firstPost = posts[0];
  const postType = firstPost.type;

  const { data: statuses = [] } = useApiQuery({
    query: queriesConfig.getPostStatus(firstPost?.id),
  });

  const [status, setStatus] = useState(null);

  const parentType = firstPost.included?.source?.type;
  const canUpdateStatus = firstPost?.permissions?.can_update_status;

  const { mutate, errors, resetErrors, isLoading } = useCommentAndStatusUpdate(
    posts,
    content,
    status,
    originalPostId,
    () => {
      setContent('');
      resetErrors();
      onSubmit();
    },
  );

  const showBudgetAlert =
    postsCount > 1 &&
    !firstPost.budget &&
    status === PostStatuses.SELECTED &&
    parentType === PostTypes.PARTICIPATORY_BUDGET;

  useEffect(() => {
    if (preSelectedStatus) {
      setStatus(preSelectedStatus);
    }
  }, [preSelectedStatus]);

  const handleContentChange = (event: any) => {
    setContent(event.target.value);
    resetErrors();
  };

  const handleStatusChange = (value: any) => {
    setStatus(value);
    resetErrors();
  };

  const handleModalClose = (trigger: any) => {
    AnalyticsService.track('Comment', 'Modal - close', `Comment Modal closed with ${trigger}`, {
      'Post Type': firstPost.type,
      'Post ID': firstPost.id,
    });

    setStatus(firstPost.status);
    setContent('');
    onClose();
  };

  const handleOnSubmit = (event: any) => {
    mutate();

    return event.preventDefault();
  };

  const statusUpdateInput = useMemo(() => {
    return (
      <Box
        sx={{
          mb: 2,
          maxWidth: { xs: '100%', sm: '50%' },
        }}
      >
        <PostStatusSelect
          value={status || ''}
          statuses={statuses}
          onChange={handleStatusChange}
          error={errors.status}
        />
      </Box>
    );
  }, [canUpdateStatus, status, firstPost, statuses, errors.status]);

  const contentInputLabel = useMemo(() => {
    if (canUpdateStatus) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            {withOptional(intl.formatMessage({ id: 'COMMENTS.CREATION.APPEND_COMMENT.IDEA' }))}
          </Typography>
        </Box>
      );
    }

    if (PostCategories.MESSAGING.includes(postType)) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            <FormattedMessage id="COMMENTS.CREATE.PRIVACY_WARNING" />
          </Typography>
        </Box>
      );
    }

    return null;
  }, [canUpdateStatus, postType]);

  const originalPostSelect = useMemo(() => {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1">
            <FormattedMessage id="POSTS.STATUS.ORIGINAL_POST.TITLE" />
          </Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <FormOriginalPost
            error={errors.originalPostId}
            posts={posts}
            handleChange={setOriginalPostId}
          />
        </Box>
      </>
    );
  }, [canUpdateStatus, posts, originalPostId, errors.originalPostId]);

  const dialogTitle = useMemo(() => {
    if (canUpdateStatus) {
      return intl.formatMessage({ id: 'POSTS.STATUS.FORM_TITLE' }, { count: postsCount });
    }

    return intl.formatMessage({ id: replyBtnTextId(firstPost.type) });
  }, [firstPost, canUpdateStatus, postsCount]);

  const contentInput = useMemo(() => {
    return (
      <Box sx={{ mb: 2 }}>
        <TextFieldDefault
          id="comment-on-post-input"
          value={content}
          onChange={handleContentChange}
          placeholder={intl.formatMessage({
            id: 'COMMENTS.CREATION.PLACEHOLDER',
          })}
          helperText={errors.content}
          error={errors.content}
          multiline
          rows={4}
          autoFocus
        />
      </Box>
    );
  }, [canUpdateStatus, content, isLoading, errors.content]);

  let onSubmitButtonText = <FormattedMessage id="BUTTONS.PUBLISH" />;

  if (PostCategories.MESSAGING.includes(postType)) {
    onSubmitButtonText = <FormattedMessage id="COMMENTS.CREATE.SUBMIT" />;
  }

  if (canUpdateStatus) {
    onSubmitButtonText = <FormattedMessage id="COMMENTS.CREATION.VALIDATE" />;
  }

  const dialogActions = (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="large"
        onClick={() => handleModalClose('Cancel Button')}
      >
        <FormattedMessage id="BUTTONS.CANCEL" />
      </Button>

      <ButtonWithLoader
        variant="contained"
        color="primary"
        size="large"
        onClick={handleOnSubmit}
        isFetching={isLoading}
        disabled={isLoading}
      >
        {onSubmitButtonText}
      </ButtonWithLoader>
    </>
  );

  return (
    <form onSubmit={handleOnSubmit}>
      <Dialog
        id={`modal-comment-and-status-update-${originalPostId}`}
        open={open}
        onClose={() => handleModalClose('Modal Close Button')}
        title={dialogTitle}
        label={dialogTitle}
        actions={dialogActions}
      >
        {canUpdateStatus ? (
          <>
            {showBudgetAlert && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="warning">
                  <FormattedMessage
                    id="POSTS.BUDGET.ALERT"
                    values={{
                      b: (chunks: any) => <b>{chunks}</b>,
                    }}
                  />
                </Alert>
              </Box>
            )}
            {statusUpdateInput}
            {status === PostStatuses.DUPLICATE && originalPostSelect}
            {contentInputLabel}
            {contentInput}
          </>
        ) : (
          <>
            {contentInputLabel}
            {contentInput}
          </>
        )}
      </Dialog>
    </form>
  );
}

CommentAndStatusUpdate.propTypes = {
  posts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  open: PropTypes.bool,
  preSelectedStatus: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default CommentAndStatusUpdate;
